'use client'

import { useEffect, useMemo, useState } from 'react'
import { toast } from '@/lib/toast'
import { saveAddressApi } from '@/lib/commerce/services/address-api'

type AddressType = 'apartment' | 'house' | 'office'

type AddressObj = {
  city: string
  duureg: string
  khoroo: string
  bair: string
  toot: string
  additionalAddress: string
  type?: AddressType
  houseNum?: string
  street?: string
  building?: string
  floor?: string
  officeName?: string
}

type UseAddressArgs = {
  address: any[]
  vendor: any
  customerAddress?: any[]
  isLoggedIn: boolean
}

export function useAddress({ address, vendor, customerAddress, isLoggedIn }: UseAddressArgs) {
  const [addressType, setAddressType] = useState<AddressType>('apartment')
  const [selectedAddress, setSelectedAddress] = useState<any>(null)
  const [addressObj, setAddressObj] = useState<AddressObj | null>(null)

  const [city, setCity] = useState('')
  const [duureg, setDuureg] = useState('')
  const [khoroo, setKhoroo] = useState('')
  const [bair, setBair] = useState('')
  const [toot, setToot] = useState('')
  const [additionalAddress, setAdditionalAddress] = useState('')
  const [houseNum, setHouseNum] = useState('')
  const [street, setStreet] = useState('')
  const [building, setBuilding] = useState('')
  const [floor, setFloor] = useState('')
  const [officeName, setOfficeName] = useState('')

  const [selectedProvince, setSelectedProvince] = useState<string | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null)
  const [selectedSubdistrict, setSelectedSubdistrict] = useState<string | null>(null)

  const filteredDistricts = useMemo(() => {
    const allDistricts = address?.find((province: any) => province.value === selectedProvince)?.children || []
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) return allDistricts
    const allowedDistrictIds = new Set(vendor.deliveryAreas.map((a: any) => a.districtId))
    return allDistricts.filter((d: any) => allowedDistrictIds.has(d.id))
  }, [address, selectedProvince, vendor?.deliveryAreas])

  const filteredSubdistricts = useMemo(() => {
    const district = filteredDistricts?.find((d: any) => d.value === selectedDistrict)
    if (!district) return []
    const allSubdistricts = district.subchildren || []
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) return allSubdistricts
    const areaEntry = vendor.deliveryAreas.find((a: any) => a.districtId === district.id)
    if (!areaEntry) return []
    if (areaEntry.khorooIds.length === 0) return allSubdistricts
    const allowedKhorooIds = new Set(areaEntry.khorooIds)
    return allSubdistricts.filter((k: any) => allowedKhorooIds.has(k.id))
  }, [filteredDistricts, selectedDistrict, vendor?.deliveryAreas])

  const provinces = useMemo(() => {
    if (!address) return []
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) {
      return address.map((province: any) => ({ value: province.value, label: province.value }))
    }
    const allowedDistrictIds = new Set(vendor.deliveryAreas.map((a: any) => a.districtId))
    return address
      .filter((province: any) => province.children?.some((d: any) => allowedDistrictIds.has(d.id)))
      .map((province: any) => ({ value: province.value, label: province.value }))
  }, [address, vendor?.deliveryAreas])

  const districts = useMemo(() => filteredDistricts.map((d: any) => ({ value: d.value, label: d.value })), [filteredDistricts])
  const subdistricts = useMemo(() => filteredSubdistricts.map((s: any) => ({ value: s.value, label: s.value })), [filteredSubdistricts])

  const isAddressDeliverable = (addr: any) => {
    if (!vendor?.deliveryAreas || vendor.deliveryAreas.length === 0) return true
    const c = address?.find((p: any) => p.value === addr.city)
    if (!c) return false
    const d = c.children?.find((dd: any) => dd.value === addr.duureg)
    if (!d) return false
    const areaEntry = vendor.deliveryAreas.find((a: any) => a.districtId === d.id)
    if (!areaEntry) return false
    if (areaEntry.khorooIds.length === 0) return true
    const k = d.subchildren?.find((kk: any) => kk.value === addr.khoroo)
    return k ? areaEntry.khorooIds.includes(k.id) : false
  }

  useEffect(() => {
    if (customerAddress && customerAddress.length > 0) {
      const deliverable = customerAddress.filter((addr: any) => isAddressDeliverable(addr))
      const defaultAddr = deliverable.find((addr: any) => addr.isDefault)
      const addressToUse = defaultAddr || deliverable[0]
      if (addressToUse) {
        setSelectedAddress(addressToUse.id)
        setAddressObj({
          city: addressToUse.city,
          duureg: addressToUse.duureg,
          khoroo: addressToUse.khoroo,
          bair: addressToUse.bair,
          toot: addressToUse.toot,
          additionalAddress: addressToUse.additionalAddress,
          type: addressToUse.type,
          houseNum: addressToUse.houseNum,
          street: addressToUse.street,
          building: addressToUse.building,
          floor: addressToUse.floor,
          officeName: addressToUse.officeName,
        })
      }
    }
  }, [customerAddress])

  useEffect(() => {
    if (address?.length > 0 && !selectedProvince) {
      const first = address[0]
      setSelectedProvince(first.value)
      setCity(first.value)
    }
  }, [address, selectedProvince])

  useEffect(() => {
    if (filteredDistricts.length > 0 && !selectedDistrict) {
      const first = filteredDistricts[0]
      setSelectedDistrict(first.value)
      setDuureg(first.value)
    }
  }, [filteredDistricts, selectedDistrict])

  useEffect(() => {
    if (filteredSubdistricts.length > 0 && !selectedSubdistrict) {
      const first = filteredSubdistricts[0]
      setSelectedSubdistrict(first.value)
      setKhoroo(first.value)
    }
  }, [filteredSubdistricts, selectedSubdistrict])

  const composeAddressObj = (): AddressObj => {
    const obj: AddressObj = { city, duureg, khoroo, bair: '', toot: '', additionalAddress, type: addressType }
    if (addressType === 'apartment') {
      obj.bair = bair
      obj.toot = toot
    } else if (addressType === 'house') {
      obj.houseNum = houseNum
      obj.street = street
    } else if (addressType === 'office') {
      obj.building = building
      obj.floor = floor
      obj.toot = toot
      if (officeName) obj.officeName = officeName
    }
    return obj
  }

  const validateNewAddress = (): { ok: true } | { ok: false; error: string } => {
    if (!city || !duureg || !khoroo) return { ok: false, error: 'Хаягийн мэдээллийг бүрэн оруулна уу' }
    if (addressType === 'apartment' && (!bair || !toot)) return { ok: false, error: 'Байр болон тоот оруулна уу' }
    if (addressType === 'house' && !houseNum) return { ok: false, error: 'Хашааны дугаар оруулна уу' }
    if (addressType === 'office' && (!building || !floor || !toot)) return { ok: false, error: 'Барилга, давхар, тоот оруулна уу' }
    if (!vendor?.canDeliverCountryside && city !== 'Улаанбаатар') {
      return { ok: false, error: 'Манай дэлгүүр зөвхөн Улаанбаатар хот дотор хүргэлт хийдэг' }
    }
    return { ok: true }
  }

  const saveNewAddress = async (): Promise<{ ok: boolean; error?: string }> => {
    const validation = validateNewAddress()
    if (!validation.ok) {
      toast.error(validation.error)
      return { ok: false, error: validation.error }
    }
    const body = composeAddressObj()
    const res = await saveAddressApi(body)
    if (!res || res.error || !res.data) {
      const error = res?.error || 'Хаяг нэмэхэд алдаа гарлаа'
      toast.error(error)
      return { ok: false, error }
    }
    toast.success('Хаягийн мэдээллийг амжилттай нэмлээ')
    return { ok: true }
  }

  const validateForCheckout = (): { ok: true } | { ok: false; error: string } => {
    if (isLoggedIn) {
      if (!selectedAddress) return { ok: false, error: 'Хаягийн мэдээллийг сонгоно уу' }
      if (!vendor?.canDeliverCountryside && addressObj?.city !== 'Улаанбаатар') {
        return { ok: false, error: 'Зөвхөн Улаанбаатар хот дотор хүргэлттэй' }
      }
      return { ok: true }
    }
    const validation = validateNewAddress()
    if (!validation.ok) return validation
    return { ok: true }
  }

  const getCheckoutAddressObj = (): AddressObj | null => {
    if (isLoggedIn) return addressObj
    return composeAddressObj()
  }

  return {
    addressType, setAddressType,
    selectedAddress, setSelectedAddress,
    addressObj, setAddressObj,
    city, setCity,
    duureg, setDuureg,
    khoroo, setKhoroo,
    bair, setBair,
    toot, setToot,
    additionalAddress, setAdditionalAddress,
    houseNum, setHouseNum,
    street, setStreet,
    building, setBuilding,
    floor, setFloor,
    officeName, setOfficeName,
    selectedProvince, setSelectedProvince,
    selectedDistrict, setSelectedDistrict,
    selectedSubdistrict, setSelectedSubdistrict,
    provinces, districts, subdistricts,
    isAddressDeliverable,
    composeAddressObj,
    validateNewAddress,
    validateForCheckout,
    saveNewAddress,
    getCheckoutAddressObj,
  }
}
